import { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { allProducts } from '@/mocks/products';

const particles = [
  { id: 1, x: '8%',  size: 4,  delay: 0,    duration: 7,  drift: '18px' },
  { id: 2, x: '18%', size: 3,  delay: 1.2,  duration: 9,  drift: '-12px' },
  { id: 3, x: '32%', size: 5,  delay: 2.5,  duration: 8,  drift: '22px' },
  { id: 4, x: '46%', size: 3,  delay: 0.7,  duration: 11, drift: '-20px' },
  { id: 5, x: '60%', size: 4,  delay: 3.1,  duration: 7,  drift: '14px' },
  { id: 6, x: '72%', size: 6,  delay: 1.8,  duration: 10, drift: '-25px' },
  { id: 7, x: '84%', size: 3,  delay: 4.0,  duration: 8,  drift: '10px' },
  { id: 8, x: '92%', size: 4,  delay: 0.4,  duration: 9,  drift: '-18px' },
  { id: 9, x: '25%', size: 3,  delay: 5.2,  duration: 12, drift: '20px' },
  { id: 10, x: '55%', size: 5, delay: 2.0,  duration: 8,  drift: '-15px' },
  { id: 11, x: '78%', size: 3, delay: 6.0,  duration: 9,  drift: '12px' },
  { id: 12, x: '40%', size: 4, delay: 3.5,  duration: 7,  drift: '-22px' },
];

const chemSymbols = [
  { label: 'H₂O', x: '75%', delay: 0,   duration: 14 },
  { label: 'CH₄', x: '82%', delay: 3,   duration: 16 },
  { label: 'NaOH', x: '88%', delay: 6,  duration: 12 },
  { label: 'O₂',  x: '70%', delay: 9,   duration: 15 },
  { label: 'CO₂', x: '93%', delay: 2,   duration: 13 },
];

export default function Hero() {
  const [visible, setVisible] = useState(false);
  const navigate = useNavigate();
  const featuredProducts = allProducts.filter(p => [1, 10, 12].includes(p.id));

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <section id="inicio" className="relative min-h-screen flex items-center overflow-hidden">

      {/* ── Background image with Ken Burns zoom ── */}
      <div className="absolute inset-0 z-0 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=Industrial%20oil%20refinery%20at%20night%20with%20dramatic%20lighting%2C%20glowing%20towers%20and%20pipes%2C%20chemical%20plant%20infrastructure%2C%20deep%20dark%20atmosphere%20with%20electric%20blue%20and%20white%20accent%20lights%2C%20ultra-wide%20panoramic%20view%2C%20cinematic%20industrial%20photography%2C%20high%20contrast%2C%20dark%20navy%20sky&width=1800&height=1000&seq=hero-chemicals-bg-01&orientation=landscape"
          alt="Planta industrial Poly Alphas Chemicals"
          className="w-full h-full object-cover object-center animate-ken-burns"
          style={{ transformOrigin: 'center center' }}
        />
        {/* Dark overlay gradients */}
        <div className="absolute inset-0 bg-gradient-to-r from-background-950/96 via-background-950/80 to-background-950/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-transparent to-background-950/30" />
      </div>

      {/* ── Aurora color breathing overlay ── */}
      <div
        className="absolute inset-0 z-0 pointer-events-none animate-aurora"
        style={{
          background: 'radial-gradient(ellipse 80% 60% at 60% 50%, oklch(var(--primary-500) / 0.10) 0%, oklch(var(--accent-500) / 0.06) 50%, transparent 75%)',
        }}
      />

      {/* ── Scan line (horizontal light sweep) ── */}
      <div
        className="absolute left-0 right-0 z-1 pointer-events-none animate-scan-line"
        style={{
          height: 2,
          background: 'linear-gradient(90deg, transparent 0%, oklch(var(--primary-400) / 0.5) 30%, oklch(var(--accent-400) / 0.8) 50%, oklch(var(--primary-400) / 0.5) 70%, transparent 100%)',
          filter: 'blur(1px)',
        }}
      />

      {/* ── Floating particles (rising dots) ── */}
      <div className="absolute inset-0 z-1 pointer-events-none overflow-hidden">
        {particles.map((p) => (
          <div
            key={p.id}
            className="absolute bottom-0 rounded-full"
            style={{
              left: p.x,
              width: p.size,
              height: p.size,
              background: `oklch(var(--primary-400) / 0.7)`,
              boxShadow: `0 0 ${p.size * 2}px oklch(var(--primary-500) / 0.5)`,
              animation: `particle-rise ${p.duration}s ease-in ${p.delay}s infinite`,
              '--drift': p.drift,
            } as React.CSSProperties}
          />
        ))}
      </div>

      {/* ── Floating chemical symbols (right side) ── */}
      <div className="absolute inset-0 z-1 pointer-events-none overflow-hidden hidden lg:block">
        {chemSymbols.map((s) => (
          <div
            key={s.label}
            className="absolute bottom-10 font-mono text-xs font-600"
            style={{
              left: s.x,
              color: 'oklch(var(--accent-400) / 0.4)',
              animation: `particle-rise ${s.duration}s ease-in ${s.delay}s infinite`,
              '--drift': '8px',
              letterSpacing: '0.1em',
            } as React.CSSProperties}
          >
            {s.label}
          </div>
        ))}
      </div>

      {/* ── Molecule orbit decoration (right) ── */}
      <div className="absolute right-8 top-1/2 -translate-y-1/2 hidden xl:flex items-center justify-center z-1">
        {/* Ring 1 */}
        <div
          className="absolute rounded-full border border-primary-400/25 animate-spin-slow animate-orbit-ping"
          style={{ width: 340, height: 340, animationDuration: '14s' }}
        />
        {/* Ring 2 */}
        <div
          className="absolute rounded-full border border-accent-400/20 animate-spin-slow"
          style={{ width: 240, height: 240, animationDirection: 'reverse', animationDuration: '9s' }}
        />
        {/* Ring 3 */}
        <div
          className="absolute rounded-full border border-primary-300/15 animate-spin-slow"
          style={{ width: 150, height: 150, animationDuration: '6s' }}
        />
        {/* Center dot */}
        <div
          className="absolute rounded-full bg-primary-500/30"
          style={{ width: 18, height: 18, boxShadow: '0 0 20px oklch(var(--primary-500)/0.5)' }}
        />
        {/* Orbiting atoms on ring 1 */}
        {[0, 120, 240].map((deg) => (
          <div
            key={deg}
            className="absolute"
            style={{
              width: 340, height: 340,
              animation: `spin-slow 14s linear infinite`,
              transform: `rotate(${deg}deg)`,
            }}
          >
            <div
              className="absolute rounded-full bg-accent-400/70"
              style={{
                width: 8, height: 8,
                top: -4, left: '50%', marginLeft: -4,
                boxShadow: '0 0 10px oklch(var(--accent-400)/0.8)',
              }}
            />
          </div>
        ))}
      </div>

      {/* ── Content ── */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-16">
        <div className="flex flex-col lg:flex-row lg:items-center gap-8 lg:gap-12">

          {/* ── Left: main content ── */}
          <div className="flex-1 max-w-2xl">

          {/* Badge */}
          <div
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary-500/40 bg-primary-500/10 mb-6 transition-all duration-700 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
            <span className="text-accent-400 text-xs font-600 tracking-widest uppercase">
              Especialistas en Química Industrial
            </span>
          </div>

          {/* Headline */}
          <h1
            className={`font-heading font-700 text-4xl md:text-6xl lg:text-7xl leading-none mb-4 text-white transition-all duration-700 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <span className="block">POLY ALPHAS&nbsp;</span>
            <span className="block gradient-text">CHEMICALS C.A.</span>
          </h1>

          {/* Subtitle */}
          <p
            className={`text-foreground-300 text-lg md:text-xl leading-relaxed max-w-xl mb-8 transition-all duration-700 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Proveedor líder de <strong className="text-white">químicos industriales</strong> y{' '}
            <strong className="text-white">fluidos de perforación</strong> para la industria
            petrolera. Calidad certificada, entrega a nivel nacional e internacional.
          </p>

          {/* CTA buttons */}
          <div
            className={`flex flex-col sm:flex-row gap-4 transition-all duration-700 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            <button
              onClick={() => navigate('/productos')}
              className="px-8 py-4 bg-primary-500 hover:bg-primary-400 text-white font-600 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap text-base animate-pulse-glow flex items-center gap-2 justify-center"
            >
              <i className="ri-test-tube-line" />
              Ver Productos
            </button>
            <button
              onClick={() => navigate('/contacto')}
              className="px-8 py-4 border border-white/30 hover:border-white/60 text-white font-600 rounded-md transition-all duration-200 cursor-pointer whitespace-nowrap text-base backdrop-blur-sm hover:bg-white/5 flex items-center gap-2 justify-center"
            >
              <i className="ri-phone-line" />
              Solicitar Cotización
            </button>
          </div>

          {/* Stats row */}
          <div
            className={`flex flex-wrap gap-8 mt-12 transition-all duration-700 delay-400 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {[
              { value: '15+', label: 'Años de Experiencia' },
              { value: '50+', label: 'Productos Químicos' },
              { value: '100%', label: 'Calidad Garantizada' },
            ].map((stat) => (
              <div key={stat.label} className="flex flex-col">
                <span className="font-heading font-700 text-3xl text-primary-400">{stat.value}</span>
                <span className="text-foreground-400 text-sm">{stat.label}</span>
              </div>
            ))}
          </div>
          </div>{/* close left content */}

          {/* ── Right: Featured Products Panel ── */}
          <div className={`hidden lg:flex flex-col w-[330px] flex-shrink-0 transition-all duration-700 delay-500 ${
            visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}>
            <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5">
              {/* Panel header */}
              <div className="flex items-center gap-2 mb-5">
                <div className="w-2 h-2 rounded-full bg-accent-400 animate-pulse" />
                <span className="text-accent-400 text-xs font-600 tracking-widest uppercase">Productos Destacados</span>
              </div>

              {/* Product cards */}
              <div className="space-y-3">
                {featuredProducts.map((product, i) => (
                  <Link
                    key={product.id}
                    to="/catalogo"
                    className={`group relative bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.06] hover:border-primary-500/30 rounded-xl p-3.5 cursor-pointer transition-all duration-300 ${
                      visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                    }`}
                    style={{ transitionDelay: `${0.65 + i * 0.1}s`, transitionProperty: 'all' }}
                  >
                    <div className="flex items-start gap-3">
                      {/* Icon */}
                      <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-500/10 group-hover:bg-primary-500/20 transition-all flex-shrink-0">
                        <i className={`${product.icon} text-primary-400 text-base`}></i>
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                          <span className="font-heading font-700 text-white text-sm leading-tight">{product.name}</span>
                          {product.badge && (
                            <span className={`text-[10px] font-600 px-1.5 py-px rounded-full text-white flex-shrink-0 ${product.badgeColor}`}>
                              {product.badge}
                            </span>
                          )}
                        </div>
                        <span className="text-accent-400/60 text-[10px] font-500 tracking-wide uppercase block mb-1">
                          {product.catalogType}
                        </span>
                        <p className="text-foreground-400/50 text-[11px] leading-relaxed line-clamp-2">
                          {product.description}
                        </p>
                      </div>
                    </div>

                    {/* Hover arrow */}
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all duration-200">
                      <i className="ri-arrow-right-s-line text-primary-400 text-sm"></i>
                    </div>
                  </Link>
                ))}
              </div>

              {/* View all button */}
              <Link
                to="/catalogo"
                className="mt-4 w-full py-2.5 rounded-xl border border-primary-500/25 hover:border-primary-500/50 text-primary-400 hover:text-primary-300 text-xs font-600 transition-all duration-200 cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 hover:bg-primary-500/[0.06]"
              >
                Ver Catálogo Completo
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
          </div>

        </div>{/* close flex row */}
      </div>

      {/* ── Scroll indicator ── */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2">
        <span className="text-foreground-500 text-xs tracking-widest uppercase">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-primary-400 to-transparent animate-pulse" />
      </div>
    </section>
  );
}